import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Plus,
  Edit,
  Save,
  X,
  Trash2,
  Target,
  TrendingUp,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
  DollarSign,
  Building,
  User,
  Settings,
  BarChart3,
  CheckCircle,
} from "lucide-react"
import { HRService } from "@/features/hr"
import { CommissionRateService } from "@/features/hr"
import type {
  CommissionRate,
  CommissionRateStats,
  CommissionRateFilters,
  CommissionRateValidationResult,
} from "@/features/hr"
import type { Organization } from "@/features/organizations"
import type { Campaign } from "@/features/campaigns"
import type { Agent } from "@/features/auth"
import { CommissionRateShimmerTable } from "@/shared/components"
import { useCampaign } from "@/features/campaigns"
import { useAuth } from "@/features/auth"

interface CommissionRateManagerProps {
  onError: (error: string) => void
}

interface NewRateForm {
  organizationId: string
  campaignId: string
  agentId: string
  commissionRate: number
  jobType: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT" | "MASTER_ORGANIZATION"
  enableSalesTier: boolean
  volumeTier1Min: number
  volumeTier1Max: number
  tier1PayRate: number
  volumeTier2Min: number
  volumeTier2Max: number
  tier2PayRate: number
  volumeTier3Min: number
  volumeTier3Max: number
  tier3PayRate: number
  overrideRate: number
  enableOverrideTier: boolean
  overrideVolumeTier1Min: number
  overrideVolumeTier1Max: number
  overrideTier1PayRate: number
  overrideVolumeTier2Min: number
  overrideVolumeTier2Max: number
  overrideTier2PayRate: number
  overrideVolumeTier3Min: number
  overrideVolumeTier3Max: number
  overrideTier3PayRate: number
  isActive: boolean
}

interface FilterState {
  search: string
  organizationId: string
  campaignId: string
  agentId: string
  jobType: string
  isActive: string
  enableSalesTier: string
  enableOverrideTier: string
  minCommissionRate: string
  maxCommissionRate: string
  minOverrideRate: string
  maxOverrideRate: string
}

export default function CommissionRateManager({ onError }: CommissionRateManagerProps) {
  const { selectedCampaign } = useCampaign()
  const { user } = useAuth()
  const [rates, setRates] = useState<CommissionRate[]>([])
  const [stats, setStats] = useState<CommissionRateStats | null>(null)
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [ratesLoading, setRatesLoading] = useState(false)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingRate, setEditingRate] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<Partial<CommissionRate>>({})
  const [validationResult, setValidationResult] = useState<CommissionRateValidationResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string; value?: any }>>([])
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  
  const [newRate, setNewRate] = useState<NewRateForm>({
    organizationId: "",
    campaignId: "",
    agentId: "",
    commissionRate: 35,
    jobType: "SALES_AGENT",
    enableSalesTier: false,
    volumeTier1Min: 1,
    volumeTier1Max: 40,
    tier1PayRate: 35,
    volumeTier2Min: 41,
    volumeTier2Max: 60,
    tier2PayRate: 40,
    volumeTier3Min: 61,
    volumeTier3Max: 80,
    tier3PayRate: 40,
    overrideRate: 35,
    enableOverrideTier: false,
    overrideVolumeTier1Min: 1,
    overrideVolumeTier1Max: 40,
    overrideTier1PayRate: 35,
    overrideVolumeTier2Min: 41,
    overrideVolumeTier2Max: 60,
    overrideTier2PayRate: 40,
    overrideVolumeTier3Min: 61,
    overrideVolumeTier3Max: 80,
    overrideTier3PayRate: 40,
    isActive: true,
  })

  const [filters, setFilters] = useState<FilterState>({
    search: "",
    organizationId: "",
    campaignId: selectedCampaign?.id || "",
    agentId: "",
    jobType: "",
    isActive: "",
    enableSalesTier: "",
    enableOverrideTier: "",
    minCommissionRate: "",
    maxCommissionRate: "",
    minOverrideRate: "",
    maxOverrideRate: "",
  })

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const allJobTypes = [
    { value: "SALES_AGENT", label: "Sales Agent" },
    { value: "LEAD_GENERATION", label: "Lead Generation" },
    { value: "EXECUTIVE_AGENT", label: "Executive Agent" },
    { value: "MASTER_ORGANIZATION", label: "Master Organization" },
  ]

  // Filter job types based on user role
  const jobTypes = useMemo(() => {
    if (user?.role === 'fmo_ceo') {
      return allJobTypes
    }
    // Hide MASTER_ORGANIZATION from non-FMO CEO users
    return allJobTypes.filter(jt => jt.value !== 'MASTER_ORGANIZATION')
  }, [user])

  // Filter organizations based on user role
  const organizations = useMemo(() => {
    if (!user || !allOrganizations.length) return []
    
    // Filter out FMO organizations for all users
    const filteredOrgs = allOrganizations.filter(org => 
      !org.name.toLowerCase().includes('fmo') && 
      !org.name.toLowerCase().includes('sample fmo')
    )
    
    switch (user.role) {
      case 'fmo_ceo':
        // FMO CEO can only see EM organizations
        return filteredOrgs.filter(org => org.type === 'EM')
      case 'hr_manager':
        // HR Manager can see DM, CM, BM, AM organizations
        return filteredOrgs.filter(org => 
          ['DM', 'CM', 'BM', 'AM'].includes(org.type)
        )
      default:
        // Default: show all non-FMO organizations
        return filteredOrgs
    }
  }, [user, allOrganizations])

  // Set jobType to MASTER_ORGANIZATION for FMO CEO users
  useEffect(() => {
    if (user?.role === 'fmo_ceo' && newRate.jobType !== 'MASTER_ORGANIZATION') {
      setNewRate(prev => ({ ...prev, jobType: 'MASTER_ORGANIZATION' as any }))
    }
  }, [user?.role, newRate.jobType])

  // Helper function to show error messages
  const showError = useCallback((message: string) => {
    setErrorMessage(message)
    onError(message)
    // Auto-clear error after 5 seconds
    setTimeout(() => setErrorMessage(null), 5000)
  }, [onError])

  // Helper function to show success toast
  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message)
    // Auto-clear toast after 3 seconds
    setTimeout(() => setToastMessage(null), 3000)
  }, [])

  const loadCommissionRates = useCallback(async () => {
    setRatesLoading(true)
    try {
      
      const apiFilters: CommissionRateFilters = {
        page: currentPage,
        limit: itemsPerPage,
      }

      // Apply filters
      if (filters.organizationId) apiFilters.organizationId = filters.organizationId
      if (filters.campaignId) apiFilters.campaignId = filters.campaignId
      if (filters.agentId) apiFilters.agentId = filters.agentId
      if (filters.jobType) apiFilters.jobType = filters.jobType as any
      if (filters.isActive !== "") apiFilters.isActive = filters.isActive === "true"
      if (filters.enableSalesTier !== "") apiFilters.enableSalesTier = filters.enableSalesTier === "true"
      if (filters.enableOverrideTier !== "") apiFilters.enableOverrideTier = filters.enableOverrideTier === "true"
      if (filters.minCommissionRate) apiFilters.minCommissionRate = parseFloat(filters.minCommissionRate)
      if (filters.maxCommissionRate) apiFilters.maxCommissionRate = parseFloat(filters.maxCommissionRate)
      if (filters.minOverrideRate) apiFilters.minOverrideRate = parseFloat(filters.minOverrideRate)
      if (filters.maxOverrideRate) apiFilters.maxOverrideRate = parseFloat(filters.maxOverrideRate)

      
      const result = await CommissionRateService.getCommissionRates(apiFilters)
      
      
      setRates(result.commissionRates || [])
      setTotalPages(result.totalPages || 0)
      setTotalItems(result.total || 0)
     } catch (error) {
      console.error("Error loading commission rates:", error)
      const errorMsg = error instanceof Error ? error.message : "Failed to load commission rates"
      showError(errorMsg)
      // Set empty state on error
      setRates([])
      setTotalPages(0)
      setTotalItems(0)
    } finally {
      setRatesLoading(false)
    }
  }, [currentPage, itemsPerPage, filters, showError])

  const loadStats = useCallback(async () => {
    try {
      const statsData = await CommissionRateService.getCommissionRateStats()
      
      setStats(statsData)
    } catch (error) {
      console.error("Error loading statistics:", error)
      const errorMsg = error instanceof Error ? error.message : "Failed to load statistics"
      // Don't show stats errors as prominently since they're not critical
      console.warn("Stats loading failed (non-critical):", errorMsg)
    }
  }, [])

  const loadInitialData = useCallback(async () => {
    try {
      setOrganizationsLoading(true)
      
      // Load essential data first (organizations, campaigns, agents)
      const [organizationsRes, campaignsRes, agentsRes] = await Promise.all([
        HRService.getOrganizations(),
        HRService.getCampaigns(),
        HRService.getAgents()
      ])

      setAllOrganizations(organizationsRes || [])
      setCampaigns(campaignsRes || [])
      setAgents(agentsRes || [])
      
      // Load stats in background (non-blocking)
      loadStats().catch(error => {
        console.warn("Stats loading failed (non-critical):", error)
      })
      
    } catch (error) {
      console.error("Error loading initial data:", error)
      const errorMsg = error instanceof Error ? error.message : "Failed to load initial data"
      showError(errorMsg)
      
      // Ensure arrays are initialized even on error
      setAllOrganizations([])
      setCampaigns([])
      setAgents([])
    } finally {
      setOrganizationsLoading(false)
    }
  }, [loadStats, showError])

  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  useEffect(() => {
    loadCommissionRates()
  }, [loadCommissionRates])

  // Auto-fill campaign when opening add form if a campaign is selected
  useEffect(() => {
    if (selectedCampaign?.id && showAddForm && !newRate.campaignId) {
      setNewRate(prev => ({ ...prev, campaignId: selectedCampaign.id }))
    }
  }, [selectedCampaign, showAddForm, newRate.campaignId])

  // Update filters when selected campaign changes
  useEffect(() => {
    if (selectedCampaign?.id) {
      setFilters(prev => ({ ...prev, campaignId: selectedCampaign.id }))
      setCurrentPage(1)
    }
  }, [selectedCampaign])

  // Handle ESC key to close modal
  // Helper function to close the add form modal
  const closeAddForm = () => {
    setShowAddForm(false)
    setErrorMessage(null)
    setValidationErrors([])
  }

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showAddForm) {
        closeAddForm()
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [showAddForm])

  const handleAddRate = async () => {
    if (isSubmitting) return
    
    try {
      setIsSubmitting(true)
      setErrorMessage(null) // Clear any previous errors
      setValidationErrors([]) // Clear any previous validation errors
      setValidationResult(null) // Clear any previous validation results
      
      
      // Client-side validation
      const validationErrors: string[] = []
      
      if (!newRate.organizationId) validationErrors.push("Organization is required")
      if (!newRate.campaignId) validationErrors.push("Campaign is required")
      // Only require agentId for non-FMO CEO users
      if (user?.role !== 'fmo_ceo' && !newRate.agentId) validationErrors.push("Agent is required")
      if (!newRate.commissionRate || newRate.commissionRate <= 0) validationErrors.push("Commission rate must be greater than 0")
      if (!newRate.overrideRate || newRate.overrideRate <= 0) validationErrors.push("Override rate must be greater than 0")
      
      // Validate tier ranges if sales tier is enabled
      if (newRate.enableSalesTier) {
        if (newRate.volumeTier1Min >= newRate.volumeTier1Max) validationErrors.push("Tier 1 min must be less than max")
        if (newRate.volumeTier2Min >= newRate.volumeTier2Max) validationErrors.push("Tier 2 min must be less than max")
        if (newRate.volumeTier3Min >= newRate.volumeTier3Max) validationErrors.push("Tier 3 min must be less than max")
        if (newRate.volumeTier1Max >= newRate.volumeTier2Min) validationErrors.push("Tier 1 max must be less than Tier 2 min")
        if (newRate.volumeTier2Max >= newRate.volumeTier3Min) validationErrors.push("Tier 2 max must be less than Tier 3 min")
      }

      if (validationErrors.length > 0) {
        showError(`Validation failed: ${validationErrors.join(', ')}`)
        return
      }

      
      const result = await CommissionRateService.createCommissionRate(newRate)
      
      // If there are validation warnings, show them but don't block success
      if (result && typeof result === 'object' && 'warnings' in result && result.warnings && result.warnings.length > 0) {
        setValidationResult({
          isValid: true,
          errors: [],
          warnings: result.warnings
        })
      } else {
        // Clear any previous validation results
        setValidationResult(null)
      }

      // Reset form but keep the add form open
      setNewRate({
        organizationId: "",
        campaignId: "",
        agentId: "",
        commissionRate: 35,
        jobType: "SALES_AGENT",
        enableSalesTier: false,
        volumeTier1Min: 1,
        volumeTier1Max: 40,
        tier1PayRate: 35,
        volumeTier2Min: 41,
        volumeTier2Max: 60,
        tier2PayRate: 40,
        volumeTier3Min: 61,
        volumeTier3Max: 80,
        tier3PayRate: 40,
        overrideRate: 35,
        enableOverrideTier: false,
        overrideVolumeTier1Min: 1,
        overrideVolumeTier1Max: 40,
        overrideTier1PayRate: 35,
        overrideVolumeTier2Min: 41,
        overrideVolumeTier2Max: 60,
        overrideTier2PayRate: 40,
        overrideVolumeTier3Min: 61,
        overrideVolumeTier3Max: 80,
        overrideTier3PayRate: 40,
        isActive: true,
      })
      
      
      // Show success toast notification
      showSuccessToast("Commission rate created successfully!")
      
      // Refresh data immediately and wait for it to complete
      try {
        await Promise.all([
          loadCommissionRates(),
          loadStats()
        ])
      } catch (refreshError) {
        console.error("Error refreshing data after commission rate creation:", refreshError)
        showError("Commission rate was created but failed to refresh the list. Please reload the page.")
      }
    } catch (error) {
      console.error("Error adding commission rate:", error)
      
      // Enhanced error handling based on HTTP status codes
      let errorMessage = "Failed to add commission rate"
      
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        
        switch (status) {
          case 400:
            // Validation errors - handle both formats
            if (responseData?.details && Array.isArray(responseData.details)) {
              // Format: {details: [{field, message, value}]}
              setValidationErrors(responseData.details) // Save the detailed errors
              const errorMessages = responseData.details.map((detail: any) => 
                `${detail.field}: ${detail.message}`
              ).join('; ')
              errorMessage = responseData?.error || `Validation Error: ${errorMessages}`
            } else if (responseData?.errors && Array.isArray(responseData.errors)) {
              // Format: {errors: ["error1", "error2"]}
              setValidationErrors([])
              errorMessage = `Validation failed: ${responseData.errors.join(', ')}`
            } else if (responseData?.error) {
              // Format: {error: "message"}
              setValidationErrors([])
              errorMessage = `Validation Error: ${responseData.error}`
            } else if (responseData?.message) {
              setValidationErrors([])
              errorMessage = responseData.message
            } else {
              setValidationErrors([])
              errorMessage = "Validation failed"
            }
            break
            
          case 409:
            // Duplicate entry
            errorMessage = responseData?.message || "Commission rate already exists for this organization, campaign, and agent combination"
            break
            
          case 401:
            errorMessage = "Unauthorized - please log in again"
            break
            
          case 403:
            errorMessage = "Forbidden - you don't have permission to perform this action"
            break
            
          case 404:
            errorMessage = "Resource not found"
            break
            
          case 500:
            errorMessage = responseData?.message || "Internal server error"
            break
            
          default:
            errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      
      showError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditRate = (rate: CommissionRate) => {
    setEditingRate(rate.id)
    setEditingData({
      commissionRate: rate.commissionRate,
      overrideRate: rate.overrideRate,
      jobType: rate.jobType,
      enableSalesTier: rate.enableSalesTier,
      enableOverrideTier: rate.enableOverrideTier,
      volumeTier1Min: rate.volumeTier1Min,
      volumeTier1Max: rate.volumeTier1Max,
      tier1PayRate: rate.tier1PayRate,
      volumeTier2Min: rate.volumeTier2Min,
      volumeTier2Max: rate.volumeTier2Max,
      tier2PayRate: rate.tier2PayRate,
      volumeTier3Min: rate.volumeTier3Min,
      volumeTier3Max: rate.volumeTier3Max,
      tier3PayRate: rate.tier3PayRate,
      overrideVolumeTier1Min: rate.overrideVolumeTier1Min,
      overrideVolumeTier1Max: rate.overrideVolumeTier1Max,
      overrideTier1PayRate: rate.overrideTier1PayRate,
      overrideVolumeTier2Min: rate.overrideVolumeTier2Min,
      overrideVolumeTier2Max: rate.overrideVolumeTier2Max,
      overrideTier2PayRate: rate.overrideTier2PayRate,
      overrideVolumeTier3Min: rate.overrideVolumeTier3Min,
      overrideVolumeTier3Max: rate.overrideVolumeTier3Max,
      overrideTier3PayRate: rate.overrideTier3PayRate,
      isActive: rate.isActive,
    })
    // Clear any existing validation errors
    setValidationResult(null)
  }

  const handleSaveEdit = async (rateId: string) => {
    try {
      setIsSubmitting(true)
      setValidationResult(null)
      
      const result = await CommissionRateService.updateCommissionRate(rateId, editingData)
      
      
      if (!result.isValid) {
        setValidationResult(result)
        return
      }

      setEditingRate(null)
      setEditingData({})
      setValidationResult(null)
      showSuccessToast("Commission rate updated successfully!")
      
      // Refresh data after update
      await Promise.all([
        loadCommissionRates(),
        loadStats()
      ])
    } catch (error) {
      console.error("Error updating commission rate:", error)
      
      let errorMessage = "Failed to update commission rate"
      
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        
        switch (status) {
          case 400:
            if (responseData?.errors && Array.isArray(responseData.errors)) {
              errorMessage = `Validation failed: ${responseData.errors.join(', ')}`
            } else if (responseData?.message) {
              errorMessage = responseData.message
            } else {
              errorMessage = "Validation failed"
            }
            break
            
          case 404:
            errorMessage = "Commission rate not found"
            break
            
          case 500:
            errorMessage = responseData?.message || "Internal server error"
            break
            
          default:
            errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      
      showError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingRate(null)
    setEditingData({})
    setValidationResult(null)
  }

  const handleDeleteRate = async (rateId: string) => {
    const confirmed = window.confirm("Are you sure you want to delete this commission rate?")
    if (!confirmed) return

    try {
      await CommissionRateService.deleteCommissionRate(rateId)
      showSuccessToast("Commission rate deleted successfully!")
      
      // Refresh data after deletion
      await Promise.all([
        loadCommissionRates(),
        loadStats()
      ])
    } catch (error) {
      console.error("Error deleting commission rate:", error)
      
      let errorMessage = "Failed to delete commission rate"
      
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        const status = apiError.response?.status
        const responseData = apiError.response?.data
        
        switch (status) {
          case 404:
            errorMessage = "Commission rate not found"
            break
            
          case 500:
            errorMessage = responseData?.message || "Internal server error"
            break
            
          default:
            errorMessage = responseData?.message || `API Error (${status}): ${apiError.response?.statusText || 'Unknown error'}`
        }
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message
      }
      
      showError(errorMessage)
    }
  }

  const getOrganizationName = (organizationId: string) => {
    return organizations?.find((o) => o.id === organizationId)?.name || organizationId
  }

  const getCampaignName = (campaignId: string) => {
    return campaigns?.find((c) => c.id === campaignId)?.name || campaignId
  }

  const getAgentName = (agentId: string) => {
    const agent = agents?.find((a) => a.id === agentId)
    if (agent && agent.user) {
      return `${agent.user.firstName} ${agent.user.lastName}`
    }
    return agentId
  }

  const getJobTypeLabel = (jobType: string) => {
    return jobTypes.find((j) => j.value === jobType)?.label || jobType
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push("...")
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push("...")
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push("...")
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push("...")
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Commission Rates Management</h2>
          <p className="text-sm text-accent-600 mt-1">Define how agents are paid per organization, campaign, and agent</p>
        </div>
        <button 
          onClick={() => setShowAddForm(true)} 
          className="btn-primary flex items-center justify-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Commission Rate</span>
        </button>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button 
              onClick={() => setErrorMessage(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="card border-green-200 bg-green-50">
          <div className="flex items-start space-x-2">
            <div className="w-5 h-5 text-green-600 mt-0.5">✓</div>
            <div>
              <h3 className="font-medium text-green-800">Success</h3>
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
            <button 
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Validation Warnings/Errors */}
      {validationResult && (
        <div className={`card ${validationResult?.isValid ? 'border-yellow-200 bg-yellow-50' : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-start space-x-2">
            <AlertTriangle className={`w-5 h-5 ${validationResult?.isValid ? 'text-yellow-600' : 'text-red-600'} mt-0.5`} />
            <div>
              <h3 className={`font-medium ${validationResult?.isValid ? 'text-yellow-800' : 'text-red-800'}`}>
                {validationResult?.isValid ? 'Warnings' : 'Validation Errors'}
              </h3>
              {validationResult?.warnings?.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-yellow-700">Warnings:</h4>
                  <ul className="list-disc list-inside text-sm text-yellow-700 mt-1">
                    {validationResult.warnings?.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    )) || []}
                  </ul>
                </div>
              )}
              {validationResult?.errors?.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-red-700">Errors:</h4>
                  <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                    {validationResult.errors?.map((error, index) => (
                      <li key={index}>{error}</li>
                    )) || []}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Rate Modal */}
      {showAddForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeAddForm}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-accent-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-accent-900">Add Commission Rate</h3>
              <button 
                onClick={closeAddForm} 
                className="text-accent-400 hover:text-accent-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <h4 className="text-md font-semibold text-accent-900 mb-6">Commission Rate Configuration</h4>
              
              {/* Error Message Inside Modal */}
              {errorMessage && showAddForm && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-medium text-red-800">Error</h3>
                      <p className="text-sm text-red-700 whitespace-pre-line mb-2">{errorMessage}</p>
                      
                      {/* Validation Errors Display */}
                      {validationErrors.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <h4 className="text-sm font-semibold text-red-900 mb-2">Validation Issues:</h4>
                          <ul className="space-y-2">
                            {validationErrors.map((validationError, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm">
                                <span className="text-red-500 font-bold mt-0.5">•</span>
                                <div className="flex-1">
                                  <span className="font-medium text-red-900 capitalize">
                                    {validationError.field.replace(/([A-Z])/g, ' $1').trim()}:
                                  </span>
                                  <span className="text-red-700 ml-1">{validationError.message}</span>
                                  {validationError.value !== undefined && (
                                    <span className="block text-xs text-red-600 mt-1">
                                      Current value: {typeof validationError.value === 'object' 
                                        ? JSON.stringify(validationError.value) 
                                        : String(validationError.value)}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => {
                        setErrorMessage(null)
                        setValidationErrors([])
                      }}
                      className="text-red-600 hover:text-red-800 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Main Configuration Row */}
              <div className={`grid grid-cols-1 gap-4 mb-6 ${user?.role === 'fmo_ceo' ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                {/* Campaign - Read Only Display */}
                <div>
                  <label className="block text-sm font-medium text-accent-700 mb-1">Campaign *</label>
                  {selectedCampaign ? (
                    <div className="flex items-center space-x-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                      <Target className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-primary-900">{selectedCampaign.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-800">No campaign selected</span>
                    </div>
                  )}
                </div>

                {/* Organization */}
                <div>
                  <label className="block text-sm font-medium text-accent-700 mb-1">Organization *</label>
                  <select
                    className="input-field"
                    value={newRate.organizationId}
                    onChange={(e) => setNewRate({ ...newRate, organizationId: e.target.value })}
                    disabled={organizationsLoading}
                  >
                    <option value="">
                      {organizationsLoading 
                        ? 'Loading organizations...'
                        : organizations.length === 0 
                        ? user?.role === 'fmo_ceo' 
                          ? 'No EM organizations available' 
                          : user?.role === 'hr_manager'
                          ? 'No DM/CM/BM/AM organizations available'
                          : 'No organizations available'
                        : 'Select Organization'
                      }
                    </option>
                    {!organizationsLoading && organizations?.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.type})
                      </option>
                    )) || []}
                  </select>
                </div>

                {/* Salesman Name - Hidden for FMO CEO */}
                {user?.role !== 'fmo_ceo' && (
                  <div>
                    <label className="block text-sm font-medium text-accent-700 mb-1">Salesman Name *</label>
                    <select
                      className="input-field"
                      value={newRate.agentId}
                      onChange={(e) => setNewRate({ ...newRate, agentId: e.target.value })}
                    >
                      <option value="">Select Salesman</option>
                      {agents?.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.user ? `${agent.user.firstName} ${agent.user.lastName}` : `${agent.agentCode}`}
                        </option>
                      )) || []}
                    </select>
                  </div>
                )}

                {/* Commission Rate */}
                <div>
                  <label className="block text-sm font-medium text-accent-700 mb-1">Commission Rate ($) *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={newRate.commissionRate || ''}
                    onChange={(e) => setNewRate({ ...newRate, commissionRate: parseFloat(e.target.value) || 0 })}
                    placeholder="35"
                    min="10"
                    max="75"
                    step="0.01"
                  />
                  {newRate.commissionRate > 50 && (
                    <p className="text-yellow-600 text-xs mt-1">⚠️ Higher pay than average sales agent</p>
                  )}
                </div>
              </div>

              {/* Job Type and Sales Tier Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Job Type - Show "Master Organization" for FMO CEO, dropdown for others */}
                {user?.role === 'fmo_ceo' ? (
                  <div>
                    <label className="block text-sm font-medium text-accent-700 mb-1">Job Type *</label>
                    <div className="flex items-center space-x-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                      <Building className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-primary-900">Master Organization</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-accent-700 mb-1">Job Type *</label>
                    <select
                      className="input-field"
                      value={newRate.jobType}
                      onChange={(e) => setNewRate({ ...newRate, jobType: e.target.value as any })}
                    >
                      <option value="SALES_AGENT">Sales Agent</option>
                      <option value="LEAD_GENERATION">Lead Generation</option>
                      <option value="EXECUTIVE_AGENT">Executive Agent</option>
                    </select>
                  </div>
                )}

                {/* Enable Sales Tier */}
                <div>
                  <label className="block text-sm font-medium text-accent-700 mb-1">Enable Sales Tier *</label>
                  <select
                    className="input-field"
                    value={newRate.enableSalesTier ? 'yes' : 'no'}
                    onChange={(e) => setNewRate({ ...newRate, enableSalesTier: e.target.value === 'yes' })}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {newRate.enableSalesTier && (
                    <p className="text-yellow-600 text-xs mt-1">⚠️ Selecting yes will bypass Commission rate</p>
                  )}
                </div>
              </div>

          {/* Volume Tiers Section */}
          <div className="mt-6">
            <h4 className="text-md font-semibold text-accent-900 mb-4">Volume Tiers Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* VOLUME TIER 1 */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 1</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier1Min || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier1Min: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="1"
                      />
                      <span className="text-accent-500">to</span>
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier1Max || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier1Max: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="40"
                      />
                    </div>
                    <p className="text-xs text-accent-500 mt-1">(individual sales count)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Tier1 Pay Rate ($)</label>
                    <input
                      type="text"
                      className="input-field text-sm"
                      value={newRate.tier1PayRate || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewRate({ ...newRate, tier1PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                        }
                      }}
                      placeholder="35"
                    />
                    <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
                  </div>
                </div>
              </div>

              {/* VOLUME TIER 2 */}
              <div className="border rounded-lg p-4 bg-green-50">
                <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 2</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier2Min || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier2Min: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="41"
                      />
                      <span className="text-accent-500">to</span>
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier2Max || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier2Max: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="60"
                      />
                    </div>
                    <p className="text-xs text-accent-500 mt-1">(individual sales count)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Tier2 Pay Rate ($)</label>
                    <input
                      type="text"
                      className="input-field text-sm"
                      value={newRate.tier2PayRate || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewRate({ ...newRate, tier2PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                        }
                      }}
                      placeholder="40"
                    />
                    <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
                  </div>
                </div>
              </div>

              {/* VOLUME TIER 3 */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <h5 className="font-medium text-accent-700 mb-3 text-center">VOLUME TIER 3</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Volume Range</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier3Min || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier3Min: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="61"
                      />
                      <span className="text-accent-500">to</span>
                      <input
                        type="text"
                        className="input-field text-sm w-24"
                        value={newRate.volumeTier3Max || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setNewRate({ ...newRate, volumeTier3Max: value === '' ? 0 : parseInt(value) });
                          }
                        }}
                        placeholder="80"
                      />
                    </div>
                    <p className="text-xs text-accent-500 mt-1">(individual sales count)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-accent-600 mb-1">Tier3 Pay Rate ($)</label>
                    <input
                      type="text"
                      className="input-field text-sm"
                      value={newRate.tier3PayRate || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewRate({ ...newRate, tier3PayRate: value === '' ? 0 : parseFloat(value) || 0 });
                        }
                      }}
                      placeholder="40"
                    />
                    <p className="text-xs text-accent-500 mt-1">(default dollar amount)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button onClick={closeAddForm} className="btn-outline">
                  Cancel
                </button>
                <button
                  onClick={handleAddRate}
                  className={`btn-primary flex items-center justify-center space-x-2 ${
                    isSubmitting 
                      ? 'opacity-75 cursor-not-allowed' 
                      : 'hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                  }`}
                  disabled={
                    !newRate.organizationId || 
                    !newRate.campaignId || 
                    (user?.role !== 'fmo_ceo' && !newRate.agentId) || 
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Adding Commission Rate...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Add Commission Rate</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="card">
        {/* Selected Campaign Display */}
        {/* <div className="mb-4 pb-4 border-b border-accent-200">
          <label className="block text-sm font-medium text-accent-700 mb-2">
            <span className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-primary-600" />
              <span>Selected Campaign</span>
            </span>
          </label>
          {selectedCampaign ? (
            <div className="flex items-center space-x-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
              <Target className="w-5 h-5 text-primary-600" />
              <span className="text-base font-medium text-primary-900">{selectedCampaign.name}</span>
            </div>
          ) : (
            <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">Please select a campaign from the dashboard to view commission rates</span>
            </div>
          )}
        </div> */}

        {/* Other Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                className="input-field pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>
          <div>
            <select
              className="input-field"
              value={filters.organizationId}
              onChange={(e) => setFilters({ ...filters, organizationId: e.target.value })}
            >
              <option value="">All Organizations</option>
              {organizations?.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              )) || []}
            </select>
          </div>
          <div>
            <select
              className="input-field"
              value={filters.agentId}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value })}
            >
              <option value="">All Agents</option>
              {agents?.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.user ? `${agent.user.firstName} ${agent.user.lastName}` : agent.agentCode}
                </option>
              )) || []}
            </select>
          </div>
          <div>
            <select
              className="input-field"
              value={filters.jobType}
              onChange={(e) => setFilters({ ...filters, jobType: e.target.value })}
            >
              <option value="">All Job Types</option>
              {jobTypes.map((jobType) => (
                <option key={jobType.value} value={jobType.value}>
                  {jobType.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Commission Rates Table */}
      <div className="card">
        {ratesLoading ? (
          <CommissionRateShimmerTable rows={itemsPerPage} />
        ) : (
          <div className="w-full max-h-[600px] overflow-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-accent-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Organization
                  </th>
                  {user?.role !== 'fmo_ceo' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Agent
                  </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Commission Rate
                  </th>
                  {user?.role !== 'fmo_ceo' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Job Type
                  </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Sales Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Volume Tiers
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-accent-200">
              {rates?.map((rate) => (
                <tr key={rate.id} className="hover:bg-accent-50">
                  {/* Organization */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-primary-600 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-accent-900">
                          {rate.organization?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-accent-500">
                          {rate.organization?.type || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Agent */}
                  {user?.role !== 'fmo_ceo' && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-accent-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-accent-900">
                          {rate.agent?.user ? `${rate.agent.user.firstName} ${rate.agent.user.lastName}` : rate.agent?.agentCode || 'N/A'}
                        </div>
                        <div className="text-sm text-accent-500">
                          {rate.agent?.agentCode || ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  )}

                  {/* Campaign */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-accent-900">
                      {rate.campaign?.name || 'N/A'}
                    </div>
                  </td>

                  {/* Commission Rate */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {editingRate === rate.id ? (
                      <input
                        type="number"
                        className="input-field w-24 text-sm"
                        value={editingData.commissionRate || ''}
                        onChange={(e) => setEditingData({ ...editingData, commissionRate: parseFloat(e.target.value) || 0 })}
                        min="10"
                        max="75"
                        step="0.01"
                      />
                    ) : (
                      <div className="text-sm font-medium text-accent-900">
                        {formatCurrency(rate.commissionRate)}
                      </div>
                    )}
                  </td>

                  {/* Job Type */}
                  {user?.role !== 'fmo_ceo' && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    {editingRate === rate.id ? (
                      <select
                        className="input-field text-sm"
                        value={editingData.jobType || rate.jobType}
                        onChange={(e) => setEditingData({ ...editingData, jobType: e.target.value as any })}
                      >
                        <option value="SALES_AGENT">Sales Agent</option>
                        <option value="LEAD_GENERATION">Lead Generation</option>
                        <option value="EXECUTIVE_AGENT">Executive Agent</option>
                      </select>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getJobTypeLabel(rate.jobType)}
                      </span>
                    )}
                  </td>
                  )}

                  {/* Sales Tier */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {editingRate === rate.id ? (
                      <select
                        className="input-field text-sm"
                        value={editingData.enableSalesTier !== undefined ? (editingData.enableSalesTier ? 'yes' : 'no') : (rate.enableSalesTier ? 'yes' : 'no')}
                        onChange={(e) => setEditingData({ ...editingData, enableSalesTier: e.target.value === 'yes' })}
                      >
                        <option value="no">Disabled</option>
                        <option value="yes">Enabled</option>
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rate.enableSalesTier 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rate.enableSalesTier ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </td>

                  {/* Volume Tiers */}
                  <td className="px-4 py-4">
                    {editingRate === rate.id ? (
                      <div className="space-y-2 min-w-[300px]">
                        {/* Tier 1 */}
                        <div className="flex items-center space-x-1 text-xs">
                          <span className="font-medium w-6">T1:</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier1Min ?? rate.volumeTier1Min}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier1Min: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier1Min: rate.volumeTier1Min });
                              }
                            }}
                          />
                          <span>-</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier1Max ?? rate.volumeTier1Max}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier1Max: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier1Max: rate.volumeTier1Max });
                              }
                            }}
                          />
                          <span>→ $</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.tier1PayRate ?? rate.tier1PayRate}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setEditingData({ ...editingData, tier1PayRate: (value === '' ? '' : parseFloat(value) || '') as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, tier1PayRate: rate.tier1PayRate });
                              }
                            }}
                          />
                        </div>
                        {/* Tier 2 */}
                        <div className="flex items-center space-x-1 text-xs">
                          <span className="font-medium w-6">T2:</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier2Min ?? rate.volumeTier2Min}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier2Min: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier2Min: rate.volumeTier2Min });
                              }
                            }}
                          />
                          <span>-</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier2Max ?? rate.volumeTier2Max}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier2Max: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier2Max: rate.volumeTier2Max });
                              }
                            }}
                          />
                          <span>→ $</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.tier2PayRate ?? rate.tier2PayRate}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setEditingData({ ...editingData, tier2PayRate: (value === '' ? '' : parseFloat(value) || '') as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, tier2PayRate: rate.tier2PayRate });
                              }
                            }}
                          />
                        </div>
                        {/* Tier 3 */}
                        <div className="flex items-center space-x-1 text-xs">
                          <span className="font-medium w-6">T3:</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier3Min ?? rate.volumeTier3Min}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier3Min: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier3Min: rate.volumeTier3Min });
                              }
                            }}
                          />
                          <span>-</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.volumeTier3Max ?? rate.volumeTier3Max}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setEditingData({ ...editingData, volumeTier3Max: (value === '' ? '' : parseInt(value)) as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, volumeTier3Max: rate.volumeTier3Max });
                              }
                            }}
                          />
                          <span>→ $</span>
                          <input
                            type="text"
                            className="input-field w-16 text-xs py-1"
                            value={editingData.tier3PayRate ?? rate.tier3PayRate}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setEditingData({ ...editingData, tier3PayRate: (value === '' ? '' : parseFloat(value) || '') as any });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                setEditingData({ ...editingData, tier3PayRate: rate.tier3PayRate });
                              }
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-accent-900">
                        <div>T1: {rate.volumeTier1Min}-{rate.volumeTier1Max} → {formatCurrency(rate.tier1PayRate)}</div>
                        <div>T2: {rate.volumeTier2Min}-{rate.volumeTier2Max} → {formatCurrency(rate.tier2PayRate)}</div>
                        <div>T3: {rate.volumeTier3Min}-{rate.volumeTier3Max} → {formatCurrency(rate.tier3PayRate)}</div>
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {editingRate === rate.id ? (
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => setEditingData({ ...editingData, isActive: !(editingData.isActive ?? rate.isActive) })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            (editingData.isActive ?? rate.isActive)
                              ? 'bg-green-600'
                              : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              (editingData.isActive ?? rate.isActive)
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-xs font-medium ${
                          (editingData.isActive ?? rate.isActive)
                            ? 'text-green-700'
                            : 'text-gray-700'
                        }`}>
                          {(editingData.isActive ?? rate.isActive) ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rate.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {rate.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {editingRate === rate.id ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(rate.id)}
                            className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-md transition-colors ${
                              isSubmitting 
                                ? 'bg-green-100 text-green-400 cursor-not-allowed' 
                                : 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-900'
                            }`}
                            disabled={isSubmitting}
                            title={isSubmitting ? 'Updating...' : 'Save changes'}
                          >
                            {isSubmitting ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent"></div>
                                <span className="text-xs">Saving...</span>
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4" />
                                <span className="text-xs">Save</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center justify-center space-x-1 px-3 py-1.5 rounded-md bg-gray-50 text-accent-600 hover:bg-gray-100 hover:text-accent-700 transition-colors"
                            disabled={isSubmitting}
                            title="Cancel editing"
                          >
                            <X className="w-4 h-4" />
                            <span className="text-xs">Cancel</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditRate(rate)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRate(rate.id)}
                            className="text-red-600 hover:text-red-900"
                            disabled={isSubmitting}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}

        {rates?.length === 0 && !ratesLoading && (
          <div className="text-center py-8">
            {!selectedCampaign ? (
              <>
                <Target className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-accent-700 font-medium mb-2">Please select a campaign from the dashboard</p>
                <p className="text-accent-500 text-sm">Commission rates are specific to each campaign</p>
              </>
            ) : (
              <>
                <Target className="w-12 h-12 text-accent-300 mx-auto mb-4" />
                <p className="text-accent-500">No commission rates found for {selectedCampaign.name}.</p>
                <button onClick={() => setShowAddForm(true)} className="btn-primary mt-4">
                  Add Commission Rate
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          {/* Results Info */}
          <div className="text-sm text-accent-600">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-accent-600">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="input-field py-1 px-2 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Pagination Navigation */}
          <div className="flex items-center space-x-1">
            {/* First Page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-accent-300 text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            {/* Previous Page */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-accent-300 text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Numbers */}
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === "number" && handlePageChange(page)}
                disabled={page === "..."}
                className={`px-3 py-2 rounded-md border text-sm ${
                  page === currentPage
                    ? "bg-primary-600 text-white border-primary-600"
                    : page === "..."
                      ? "border-accent-300 text-accent-400 cursor-default"
                      : "border-accent-300 text-accent-500 hover:bg-accent-50"
                }`}
              >
                {page}
              </button>
            ))}

            {/* Next Page */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-accent-300 text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last Page */}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-accent-300 text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right-full duration-300">
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setToastMessage(null)}
                className="text-green-600 hover:text-green-800 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}